import { useEffect, useState } from "react";
import axiosInstance from "../../api/axiosInstance";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { toast } from "react-toastify";

export default function PatientsList() {
  const [patients, setPatients] = useState([]);
  const [query, setQuery] = useState("");
  const [sortKey, setSortKey] = useState("name");
  const [showPopup, setShowPopup] = useState(false);

  const [newPatientName, setNewPatientName] = useState("");
  const [newPatientPhone, setNewPatientPhone] = useState("");
  const [createError, setCreateError] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  // Fetch only when query changes (search API)
  useEffect(() => {
    // Don't search if empty
    if (query.trim() === "") {
      setPatients([]);
      return;
    }

    const timeout = setTimeout(() => {
      axiosInstance
        .get(`/profile/search-patients?query=${query}`)
        .then((res) => setPatients(res.data))
        .catch(() => setPatients([]));
    }, 400); // Debounce

    return () => clearTimeout(timeout);
  }, [query]);

  const handleCreateNewPatient = () => {
    setShowPopup(true);
    setNewPatientName("");
    setNewPatientPhone("");
    setCreateError("");
  };

  const submitCreatePatient = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPatientName.trim() || !newPatientPhone.trim()) {
      setCreateError("Name and Phone are required.");
      return;
    }
    if (!/^\d{10}$/.test(newPatientPhone)) {
      setCreateError("Phone number must be 10 digits.");
      return;
    }

    setIsCreating(true);
    setCreateError("");

    try {
      await axiosInstance.post("/patient-create", {
        name: newPatientName,
        phone: newPatientPhone,
      });
      setShowPopup(false);
      // Optionally run search again if query matches new patient
      if (query) {
        setQuery(newPatientPhone); // Trigger effect? Or just clear it. 
      }
      toast.success("Patient created successfully!");
    } catch (err: any) {
      setCreateError(err.response?.data?.message || err.message || "Failed to create patient");
    } finally {
      setIsCreating(false);
    }
  };

  const filtered = patients
    .sort((a: any, b: any) => {
      if (!a[sortKey]) return -1;
      if (!b[sortKey]) return 1;
      return String(a[sortKey]).localeCompare(String(b[sortKey]));
    });

  return (
    <div className="p-6 min-h-screen bg-gray-100">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-3xl font-bold text-indigo-700">Patients List</h1>
        <button
          onClick={() => { handleCreateNewPatient() }}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
        >
          Create New Patient
        </button>
      </div>

      {showPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 relative overflow-hidden">
            <div className="bg-indigo-600 px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-bold text-white">Add New Patient</h2>
              <button
                onClick={() => setShowPopup(false)}
                className="text-white hover:text-gray-200 transition"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={submitCreatePatient} className="p-6 space-y-4">
              <div>
                <label className="block text-gray-700 font-medium mb-1">Name</label>
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition"
                  placeholder="Enter patient name"
                  value={newPatientName}
                  onChange={e => setNewPatientName(e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-1">Phone Number</label>
                <input
                  type="tel"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition"
                  placeholder="Enter 10-digit phone"
                  value={newPatientPhone}
                  onChange={e => setNewPatientPhone(e.target.value)}
                  maxLength={10}
                  required
                />
              </div>

              {createError && (
                <div className="text-red-600 text-sm bg-red-50 p-2 rounded border border-red-200">
                  {createError}
                </div>
              )}

              <div className="flex justify-end gap-3 mt-2">
                <button
                  type="button"
                  onClick={() => setShowPopup(false)}
                  className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isCreating}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {isCreating ? "Creating..." : "Create Patient"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Search */}

      <div className="flex justify-between items-center mb-4">
        <input
          type="text"
          placeholder="Search by name or phone..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="w-full mb-4 px-3 py-2 border rounded-lg"
        />

        {/* Sort */}
        <select
          value={sortKey}
          onChange={(e) => setSortKey(e.target.value)}
          className="mb-4 px-3 py-2 border rounded-lg"
        >
          <option value="name">Sort by Name</option>
          <option value="phone">Sort by Phone</option>
        </select>
      </div>
      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="w-full">
          <thead className="bg-indigo-600 text-white">
            <tr>
              <th className="p-3 text-left">Profile</th>
              <th className="p-3 text-left">Name</th>
              <th className="p-3 text-left">Phone</th>
              <th className="p-3 text-left">Email</th>
              <th className="p-3 text-left">Reports</th>
            </tr>
          </thead>

          <tbody>

            {filtered.map((p: any) => (
              <tr key={p.phone} className="border-b">
                <td className="p-3">
                  <img
                    src={p.profile}
                    alt={p.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                </td>

                <td className="p-3 font-semibold">{p.name}</td>
                <td className="p-3">{p.phone}</td>
                <td className="p-3">{p.email}</td>

                <td className="p-3">
                  <a
                    href={`/patients/${p.phone}/reports`}
                    className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                  >
                    View Reports
                  </a>
                </td>
              </tr>
            ))}

            {filtered.length === 0 && (
              <tr>
                <td colSpan={5} className="p-4 text-center text-gray-500">
                  No patients found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
